import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DealerType } from '@1po/1po-bff-fe-spec/generated/backoffice/promotion/model/Promotion';
import { TFunction } from 'i18next';
import { FileUploadPreview, FileUploadStrip } from 'components/FileUpload';
import {
  getUploadedTargetedAudienceFromFile,
  setUploadedTargetedAudienceFromFileSizeAndName,
  uploadTargetedAudienceFileSaga,
} from 'domains/promotion/Promotion.store';
import { CreatePromotionStepProps, DealerTypeLocal } from 'domains/promotion/Promotion.types';
import { getDealerType } from 'domains/user';
import { validateFile } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/common';
import { Checkbox, Flex, MarginBox, Text } from 'UI';
import { useBreakpointSelectorFull } from 'utils';

export const getDealerTypeLabel = (dealerType: DealerType, t: TFunction) => {
  switch (dealerType) {
    case 'R1':
      return t('backoffice.promotion.target_audience.r1', 'R1');
    case 'R2':
      return t('backoffice.promotion.target_audience.r2', 'R2');
    case 'R3':
      return t('backoffice.promotion.target_audience.r3', 'R3');
    case 'R3_MOTRIO':
      return t('backoffice.promotion.target_audience.r3_motrio', 'R3 Motrio');
    default:
      return '';
  }
};

export const TargetSection = ({ promotion, setPromotion, 'data-cy': dataCy }: CreatePromotionStepProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const uploadedTargetedAudience = useSelector(getUploadedTargetedAudienceFromFile);
  const userProfileType = useSelector(getDealerType);
  const dealerTypes: DealerType[] =
    userProfileType === 'R1' ? ['R2', 'R3', 'R3_MOTRIO'] : ['R1', 'R2', 'R3', 'R3_MOTRIO'];
  const itemCount = useBreakpointSelectorFull()({ xs: 40, sm: 40, md: 50, lg: 50, xl: 48, xml: 48, xxl: 80 });

  function uploadFile(fileName: string, fileSize: number, fileBase64: string) {
    setPromotion({
      ...promotion,
      targetAudience: {},
      targetAudienceType: 'DEALERS',
    });
    dispatch(uploadTargetedAudienceFileSaga({ fileBase64 }));
    dispatch(setUploadedTargetedAudienceFromFileSizeAndName({ fileName, fileSize }));
  }

  return (
    <div data-cy={dataCy}>
      <MarginBox mt={45} />
      <Text type="heading3">{t('backoffice.promotion.target_audience', 'Target audience')}</Text>
      <Text type={'text'}>
        {t(
          'backoffice.promotion.target_audience.description',
          'Please identify the set of customers you intend to focus on and serve the marketing content.',
        )}
      </Text>
      <MarginBox mt={15} />
      <Flex direction={'row'}>
        {dealerTypes.map((dealer, i) => {
          const promotionDealer = promotion.targetAudience.dealerTypes?.find((d) => d.dealerType === dealer);
          const isChecked = promotion.targetAudience.dealerTypes?.map((d) => d.dealerType).includes(dealer);
          const isRemovable = promotionDealer ? promotionDealer.removable : true;
          return (
            <Flex key={'promotion_dealer_checkbox_' + i} data-cy={'target-audience-checkbox'}>
              <Checkbox
                checked={isChecked}
                disabled={
                  !isRemovable ||
                  (promotion.uploadedTargetedAudienceFromFile.rows &&
                    promotion.uploadedTargetedAudienceFromFile.rows.length > 0)
                }
                onChange={(v) => {
                  const checked = v;
                  const currentDealers = promotion.targetAudience.dealerTypes ?? [];
                  if (checked) {
                    setPromotion({
                      ...promotion,
                      targetAudienceType: 'NETWORK',
                      targetAudience: {
                        ...promotion.targetAudience,
                        dealerTypes: [
                          ...currentDealers,
                          {
                            dealerType: dealer,
                            removable: isRemovable,
                          } as DealerTypeLocal,
                        ],
                      },
                    });
                  } else {
                    setPromotion({
                      ...promotion,
                      targetAudienceType: 'NETWORK',
                      targetAudience: {
                        ...promotion.targetAudience,
                        dealerTypes: currentDealers.filter((d) => d.dealerType !== dealer),
                      },
                    });
                  }
                }}
                data-cy={`target-audience-checkbox-${dealer}`}
                label={
                  <Text type="base" color="grey35">
                    {getDealerTypeLabel(dealer, t)}
                  </Text>
                }
              />
            </Flex>
          );
        })}
        <Flex size={4} />
      </Flex>
      <MarginBox mt={15} />
      <Flex direction={'column'}>
        <Text type={'h5_bold'}>{t('backoffice.promotion.or_upload_dealer_list', 'or Upload dealer list')}</Text>
        <MarginBox mt={15} />
        <FileUploadStrip
          data-cy={'target-audience-file-upload'}
          handleFileBase64={uploadFile}
          validateFile={validateFile(t)}
          status={uploadedTargetedAudience.status}
          fileName={uploadedTargetedAudience.fileName}
          template={{
            link: '/fileTemplates/promotion_targeted_audience.csv',
            fileName: 'ID Tresor.csv',
          }}
          disable={
            promotion.editing ||
            (promotion.targetAudience?.dealerTypes && promotion.targetAudience.dealerTypes.length > 0)
          }
        />
        <MarginBox mt={15} />
        <FileUploadPreview
          fileSize={uploadedTargetedAudience.fileSize}
          fileName={uploadedTargetedAudience.fileName}
          columnName={t('backoffice.promotion.id_tresor', 'ID Tresor')}
          data={uploadedTargetedAudience.rows}
          loadMoreBy={itemCount}
          promotion={promotion}
          setPromotion={setPromotion}
          fileId={uploadedTargetedAudience.fileId}
          dealers={true}
          setCurrentStep={() => undefined}
        />
      </Flex>
    </div>
  );
};
